import {
  SeedingStrategy,
  SplitVolDict,
  Worklist,
  WorklistValues,
} from "../shared/interfaces";
import { is96WPlate } from "../shared/PlateHelpers";
import {
  extractPlateCode,
  generateMrackPlateCode,
  getColIndexFromSelectedWell,
  getPlateWellIdFromRowAndColIndex,
  getRowIndexFromSelectedWell,
} from "../shared/WorklistHelpers";
import {
  DestPlateInfoState,
  IntermediatePlateInfoState,
  SourcePlateInfoState,
  WorklistValuesState,
  WorklistWellMappingState,
} from "./state";
import { IExternalThawMethodSettings } from "./state/initial-state";

export enum CellNumberUpdateType {
  ALL = "All",
  EMPTY = "Empty",
  INDIVIDUAL = "Individual",
}

export const intToDestWellMappingsPresent = (
  worklistValues: WorklistValuesState
) => {
  return worklistValues.int1ToDest.length;
};

export const buildExternalThawTubeWorklist = (
  sourcePlateInfo: SourcePlateInfoState[]
) => {
  const worklist: Worklist[] = [];

  for (const sourcePlate of sourcePlateInfo) {
    if (sourcePlate.plateBarcode === "") {
      continue;
    }
    const sourcePlateType = sourcePlate.labwareTypeCode;
    const sourcePlateBarcode = sourcePlate.plateBarcode;

    for (const sourceWellInfo of sourcePlate.wellInfo) {
      const sourceWellId = getPlateWellIdFromRowAndColIndex(
        sourceWellInfo.rowPos - 1,
        sourceWellInfo.colPos - 1
      );

      worklist.push({
        task: "ExternalThawTube",
        details: `TubeBarcode|${sourceWellInfo.barcode}`,
        aspirateVol: "",
        dispenseVol: "",
        transferVol: "",
        sourcePlateType: sourcePlateType,
        sourcePlateBarcode: sourcePlateBarcode,
        sourceWellID: sourceWellId,
        destinationPlateType: "",
        destinationPlateBarcode: "",
        destinationWellID: "",
      });
    }
  }
  return worklist;
};

export const buildIntermediatWellsWorklist = (
  harvestWells: any[],
  sourcePlateInfo: any[],
  intPlateInfo: IntermediatePlateInfoState[]
) => {
  const worklist: Worklist[] = [];
  for (const harvestWellMapping of harvestWells) {
    const currentSourcePlate =
      sourcePlateInfo[harvestWellMapping.sourcePlateIndex];
    const intPlate = intPlateInfo[0];
    const index = currentSourcePlate.wellInfo.findIndex(
      (e: any) =>
        e.rowPos - 1 ===
          getRowIndexFromSelectedWell(harvestWellMapping.sourceWellId) &&
        e.colPos - 1 ===
          getColIndexFromSelectedWell(harvestWellMapping.sourceWellId)
    );
    if (index > -1)
      worklist.push({
        task: "IntermediatePlate",
        details: "",
        aspirateVol: "",
        dispenseVol: `${intPlate.resuspensionVol}`,
        transferVol: "",
        sourcePlateType: currentSourcePlate.labwareTypeCode,
        sourcePlateBarcode: currentSourcePlate.plateBarcode,
        sourceWellID: harvestWellMapping.sourceWellId,
        destinationPlateType: intPlate.labwareTypeCode,
        destinationPlateBarcode: intPlate.plateBarcode,
        destinationWellID: harvestWellMapping.destWellId,
      });
  }
  return worklist;
};

export const buildDeadTotalPlateWorklist = (
  methodSettings: IExternalThawMethodSettings,
  deadPlateType: string,
  deadPlateBarcode: string
): Worklist => {
  return {
    task: "DeadTotalPlate",
    details: `ExperimentSettings|${methodSettings.experimentSettings};AliquotNumber|${methodSettings.numberOfAliquots};IncubationTime|${methodSettings.incubationTime};`,
    aspirateVol: "",
    dispenseVol: "",
    transferVol: `${methodSettings.aliquotVolume}`,
    sourcePlateType: "",
    sourcePlateBarcode: "",
    sourceWellID: "",
    destinationPlateType: deadPlateType,
    destinationPlateBarcode: deadPlateBarcode,
    destinationWellID: "",
  };
};

export const buildSeedingStrategyWorklist = (
  methodSettings: IExternalThawMethodSettings
): Worklist => {
  const seedingStrategyString =
    methodSettings.seedingStrategy === SeedingStrategy.CellCount
      ? "Targeted"
      : "Volumetric";

  return {
    task: "SeedingStrategy",
    details: `SeedingStrat|${seedingStrategyString};`,
    aspirateVol: "",
    dispenseVol: "",
    transferVol: "",
    sourcePlateType: "",
    sourcePlateBarcode: "",
    sourceWellID: "",
    destinationPlateType: "",
    destinationPlateBarcode: "",
    destinationWellID: "",
  };
};

export const buildInt1ToDestWorklist = (
  int1ToDest: WorklistWellMappingState[],
  intPlateInfo: IntermediatePlateInfoState[],
  destPlateInfo: DestPlateInfoState[]
) => {
  const worklist: Worklist[] = [];

  for (const destWellMapping of int1ToDest) {
    const destPlateIndex = destPlateInfo.findIndex(
      (item) => item.index === destWellMapping.destPlateIndex
    );
    worklist.push({
      task: "DestinationPlate",
      details: `Preprocess|${+!!destPlateInfo[destPlateIndex]
        .preprocess};Topup|${+!!destPlateInfo[destPlateIndex]
        .topup};StartInIncubator|${+!!destPlateInfo[destPlateIndex]
        .startsInIncubator};EndInIncubator|${+!!destPlateInfo[destPlateIndex]
        .endsInIncubator};TopupVolume|${
        destPlateInfo[destPlateIndex].topup
          ? destPlateInfo[destPlateIndex].topUpVol
          : ""
      };`,
      aspirateVol: "",
      dispenseVol: "",
      transferVol: `${destWellMapping.transferVol ?? 0}`,
      sourcePlateType: intPlateInfo[0].labwareTypeCode,
      sourcePlateBarcode: intPlateInfo[0].plateBarcode,
      sourceWellID: destWellMapping.sourceWellId,
      destinationPlateType: destPlateInfo[destPlateIndex].labwareTypeCode,
      destinationPlateBarcode: destPlateInfo[destPlateIndex].plateBarcode,
      destinationWellID: destWellMapping.destWellId,
    });
  }
  return worklist;
};

export const buildBatchedDestProcessingWorklist = (
  batchDestPlates: boolean
): Worklist => {
  return {
    task: "BatchedDestProcessing",
    details: `BatchedDestProcessing|${+!!batchDestPlates}`,
    aspirateVol: "",
    dispenseVol: "",
    transferVol: "",
    sourcePlateType: "",
    sourcePlateBarcode: "",
    sourceWellID: "",
    destinationPlateType: "",
    destinationPlateBarcode: "",
    destinationWellID: "",
  };
};

export const buildIntCentrifugationWorklist = (
  methodSettings: IExternalThawMethodSettings
): Worklist => {
  return {
    task: "IntermediateCentrifugation",
    details: `VSpinRPM|${methodSettings.spinParamPercent};VSpinTime|${
      methodSettings.spinTime * 60
    };VSpinAccel|${methodSettings.spinAccel};VSpinDecel|${
      methodSettings.spinDecel
    };`,
    aspirateVol: "",
    dispenseVol: "",
    transferVol: "",
    sourcePlateType: "",
    sourcePlateBarcode: "",
    sourceWellID: "",
    destinationPlateType: "",
    destinationPlateBarcode: "",
    destinationWellID: "",
  };
};
const getSourcePlateCode = (plateBarcode: string) => {
  if (plateBarcode.startsWith("MRACK")) {
    return generateMrackPlateCode(plateBarcode);
  }
  return extractPlateCode(plateBarcode);
};

export const buildMappingFileWorklist = (
  sourcePlateInfo: SourcePlateInfoState[],
  intPlateInfo: IntermediatePlateInfoState[],
  destPlateInfo: DestPlateInfoState[]
) => {
  const worklist: Worklist[] = [];
  const uniqueSourcePlateCodes = [
    ...new Set(
      sourcePlateInfo
        .map((e) => getSourcePlateCode(e.plateBarcode))
        .filter((labware) => labware !== "")
    ),
  ];
  const uniqueDestPlateCodes = [
    ...new Set(
      destPlateInfo
        .map((e) => extractPlateCode(e.plateBarcode))
        .filter((labware) => labware !== "")
    ),
  ];

  const srcPlateCode =
    uniqueSourcePlateCodes.length > 1 ? "X" : uniqueSourcePlateCodes[0];
  const destPlateCode =
    uniqueDestPlateCodes.length > 1 ? "Y" : uniqueDestPlateCodes[0];
  const intPlateCode = "Int" + srcPlateCode + "-" + destPlateCode;
  const deadPlateCode = "Dead" + srcPlateCode + "-" + destPlateCode;
  worklist.push({
    task: "SrcToIntMappingFileKeyword",
    sourcePlateBarcode: "_" + srcPlateCode + "_to_" + intPlateCode + "_",
  });
  worklist.push({
    task: "IntToDeadMappingFileKeyword",
    sourcePlateBarcode: "_" + intPlateCode + "_to_" + deadPlateCode + "_",
  });
  worklist.push({
    task: "IntToDestMappingFileKeyword",
    sourcePlateBarcode: "_" + intPlateCode + "_to_" + destPlateCode + "_",
  });

  return worklist;
};

export const buildSplitVolDictionary = (
  intOneToDest: WorklistValues[]
): SplitVolDict => {
  const splitVolDict = intOneToDest.reduce((acc, curr) => {
    const { sourceWellId, transferVol } = curr;

    const volumeString = transferVol && transferVol !== "" ? transferVol : "0";
    const parsedVolume = parseInt(volumeString, 10);
    const volume = isNaN(parsedVolume) ? 0 : parsedVolume;

    if (!acc[sourceWellId]) {
      acc[sourceWellId] = { numSplits: 0, volUsed: 0 };
    }

    acc[sourceWellId].numSplits += 1;
    acc[sourceWellId].volUsed += volume;

    return acc;
  }, {} as SplitVolDict);
  return splitVolDict;
};

export const getSplitEvenlyTransferVol = (
  pelletResuspenionVol: number,
  numDeadAliquots: number,
  deadAliquotVol: number,
  wellId: string,
  splitVolDict: SplitVolDict
) => {
  const numSplits = splitVolDict[wellId].numSplits;
  const totalDeadAliquotVol = numDeadAliquots * deadAliquotVol;

  const splitEvenlyTransferVol =
    (pelletResuspenionVol - totalDeadAliquotVol) / numSplits;

  return Math.floor(splitEvenlyTransferVol);
};

export const getVolRemaining = (
  srcWellId: string,
  splitVolDict: SplitVolDict,
  resuspensionVol: number,
  numberOfAliquots: number,
  aliquotVolume: number
) => {
  const volUsed = splitVolDict[srcWellId]?.volUsed ?? resuspensionVol ?? 0;
  const totalDeadAliquotVol = numberOfAliquots * aliquotVolume;
  const volRemaining = resuspensionVol - totalDeadAliquotVol - volUsed;
  return volRemaining;
};

export const sourcePlateIsNotColSorted = (
  sourcePlateInfo: SourcePlateInfoState[]
) => {
  return sourcePlateInfo.some((sourcePlate) => {
    const sourceWells = [...sourcePlate.wellInfo];
    if (sourceWells.length === 0) return false;

    const sortedSourceWells = sourceWells.sort(
      (a, b) => a.colPos - b.colPos || a.rowPos - b.rowPos
    );

    const firstWell = sortedSourceWells[0];
    if (firstWell.rowPos !== 1 || firstWell.colPos !== 1) {
      return true;
    }

    const maxRowIndex = sourcePlate.rows.length;

    for (let i = 1; i < sortedSourceWells.length; i++) {
      const prevWell = sortedSourceWells[i - 1];
      const currWell = sortedSourceWells[i];

      if (prevWell.colPos === currWell.colPos) {
        //same column
        if (currWell.rowPos !== prevWell.rowPos + 1) {
          return true;
        }
      } else if (currWell.colPos === prevWell.colPos + 1) {
        //one column up
        if (prevWell.rowPos !== maxRowIndex || currWell.rowPos !== 1) {
          return true;
        }
      } else {
        //multiple columns up
        return true;
      }
    }
    return false;
  });
};

const multipleDestPlates = (destPlates: DestPlateInfoState[]) => {
  const usedDestPlates = destPlates.filter(
    (destPLate) => destPLate.labwareTypeCode !== ""
  );
  return usedDestPlates.length > 1 ? true : false;
};

const allDestPlatesSameLabware = (destPlates: DestPlateInfoState[]) => {
  const uniqueDestPlateTypes = [
    ...new Set(
      destPlates

        .map((e) => e.labwareTypeCode)
        .filter((labware) => labware !== "")
    ),
  ];

  return uniqueDestPlateTypes.length === 1 ? true : false;
};

const not96wAndNotStartInIncubator = (destPlates: DestPlateInfoState[]) => {
  return destPlates.every(
    (destPlate) =>
      !is96WPlate(destPlate.rows.length, destPlate.cols.length) &&
      destPlate.startsInIncubator === false
  );
};

export const isBatchPlatesCandidate = (destPlates: DestPlateInfoState[]) => {
  return (
    multipleDestPlates(destPlates) &&
    allDestPlatesSameLabware(destPlates) &&
    not96wAndNotStartInIncubator(destPlates)
  );
};
